import { Component, computed, inject, signal } from '@angular/core';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { SolicitacaoServicoStore, acceptDosFormatos, formatarTamanho } from '../solicitacao-servico.store';

/** Texto da etapa quando o órgão não escreveu orientação própria no adm. */
const ORIENTACAO_PADRAO =
  'Anexe, se tiver, documentos, fotos ou comprovantes que ajudem o órgão a analisar a sua solicitação.';

/**
 * A etapa Anexos, com as regras que o SERVIÇO declara no adm: se aceita anexo, se é
 * obrigatório, quantos, de que formatos e de que tamanho. Os arquivos (com o `File`) vivem no
 * store do fluxo — a etapa pode ser destruída e recriada sem perder nada.
 *
 * A conferência aqui é de conveniência: o back-end repete todas as regras e ainda confere o
 * tipo REAL de cada arquivo pelos bytes.
 */
@Component({
  selector: 'app-etapa-anexos',
  templateUrl: './etapa-anexos.component.html',
  styleUrls: ['./etapa-anexos.component.css'],
  imports: [LucideModule],
})
export class EtapaAnexosComponent {
  protected readonly store = inject(SolicitacaoServicoStore);

  readonly arquivos = this.store.arquivos;

  readonly regras = this.store.regrasAnexo;

  readonly permiteAnexos = computed(() => this.regras()?.bitPermitirAnexosSrv ?? false);

  readonly obrigatorio = computed(() => this.regras()?.bitAnexoObrigatorioSrv ?? false);

  readonly orientacao = computed(() => this.regras()?.desOrientacoesAnexoSrv?.trim() || ORIENTACAO_PADRAO);

  readonly accept = computed(() => acceptDosFormatos(this.regras()?.formatos ?? []));

  readonly resumoRegras = computed(() => {
    const regras = this.regras();

    if (!regras) return '';

    return `Formatos: ${regras.formatos.join(', ')} · até ${formatarTamanho(regras.numTamanhoMaximoBytes)} por arquivo · no máximo ${regras.numQuantidadeMaximaSrv} arquivo(s).`;
  });

  /** O limite de quantidade foi atingido: a área de seleção deixa de aceitar arquivo. */
  readonly lotado = computed(() => this.arquivos().length >= (this.regras()?.numQuantidadeMaximaSrv ?? 0));

  readonly pendenciaObrigatorio = computed(
    () => this.store.pendenciasVisiveis() && this.store.anexoObrigatorioPendente(),
  );

  readonly recusas = signal<readonly string[]>([]);

  readonly arrastando = signal(false);

  aoSelecionar(evento: Event): void {
    const campo = evento.target as HTMLInputElement;
    this.adicionar(campo.files);
    campo.value = '';
  }

  aoArrastar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(true);
  }

  aoSairDaArea(): void {
    this.arrastando.set(false);
  }

  aoSoltar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(false);
    this.adicionar(evento.dataTransfer?.files);
  }

  remover(id: number): void {
    this.store.removerArquivo(id);
    this.recusas.set([]);
  }

  formatarTamanho(bytes: number): string {
    return formatarTamanho(bytes);
  }

  private adicionar(lista: FileList | null | undefined): void {
    this.recusas.set(this.store.adicionarArquivos(lista));
  }
}
